import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Link as LinkIcon, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function statusLabel(status) {
  if (status === null) return "Loading…";
  if (!status.configured) return "OAuth not configured";
  if (status.connected) return `Connected · ${status.email}`;
  return "Not connected";
}

export default function AdminCalendarPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/status`);
      setStatus(data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/login`);
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--gg-surface)] py-16">
      <div className="max-w-2xl mx-auto px-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--gg-text-muted)] hover:text-[var(--gg-primary)] mb-6">
          <ArrowLeft size={16} /> Admin hub
        </Link>
        <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
          Google Calendar — Admin
        </h1>
        <p className="text-[var(--gg-text)] mt-2">
          Connect the business Google account (<code>info@caregracefulguidance.com</code>) once.
          After that, every paid consultation booking will automatically create a
          Calendar event and a Google Meet link.
        </p>

        {params.get("err") && (
          <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4 flex gap-3 items-start" data-testid="calendar-err">
            <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
            <div className="text-sm text-red-800">
              Google returned an error: <code>{params.get("err")}</code>. Please try connecting again — if the problem persists, verify that this callback URL is whitelisted in Google Cloud Console.
            </div>
          </div>
        )}
        {params.get("ok") && (
          <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex gap-3 items-start" data-testid="calendar-ok">
            <Check className="text-emerald-700 flex-shrink-0" size={18} />
            <div className="text-sm text-emerald-900">
              Connected as <strong>{params.get("email")}</strong>. Google Calendar & Meet auto-creation are live.
            </div>
          </div>
        )}

        <div className="mt-8 bg-white border border-[var(--gg-border)] rounded-2xl p-6" data-testid="calendar-status-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gg-text-muted)] font-semibold">Status</div>
              <div className="font-serif text-2xl text-[var(--gg-deep)] mt-1">
                {statusLabel(status)}
              </div>
              {status?.updated_at && (
                <div className="text-xs text-[var(--gg-text-muted)] mt-1">Last connected: {new Date(status.updated_at).toLocaleString()}</div>
              )}
            </div>
            <div className={`w-3 h-3 rounded-full ${status?.connected ? "bg-emerald-500" : "bg-gray-300"}`} />
          </div>

          <button
            onClick={connect}
            disabled={loading || !status?.configured}
            data-testid="calendar-connect"
            className="mt-6 bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm inline-flex items-center gap-2"
          >
            <LinkIcon size={15} />
            {status?.connected ? "Reconnect Google Calendar" : "Connect Google Calendar"}
          </button>

          <p className="text-xs text-[var(--gg-text-muted)] mt-4">
            You&apos;ll be redirected to Google to sign in as <code>info@caregracefulguidance.com</code>
            and grant permission to create calendar events on your behalf. This
            grants access <strong>only to this app</strong>, and can be revoked
            any time from your Google account&apos;s security settings.
          </p>
        </div>
      </div>
    </div>
  );
}
